import React from 'react';
import { X, CheckCircle, FilePlus, Trash2 } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { formatQuantity } from 'core/utils/units';
import { banfSubject, banfDisplayStatus, banfVendor, banfMatkl, fmtAmount, waitingSince } from './banfListUtils';

/**
 * Detail-Drawer (Design-Handoff §4): rechts fixiert, Scrim, Metagrid + Positionen +
 * Genehmigungsweg-Stepper. Der Stepper zeigt nur ECHTE Zustände (Angelegt -> Genehmigung ->
 * SAP-Übertragung) — die Design-Schritte "Fachliche Prüfung/Freigabe Einkauf" kommen erst
 * mit dem WF-Modul (F13) und werden nicht vorgetäuscht.
 */
const BanfDetailDrawer = ({ request, canDecide, canDelete, processing, onClose, onApprove, onReject, onReorder, onDelete, onRetrySap }) => {
  const { isDarkMode } = useTheme();
  const { t, language } = useLanguage();

  if (!request) return null;
  const items = Array.isArray(request.items) ? request.items : [];
  const status = banfDisplayStatus(request);
  const fmtDate = (d) => (d ? new Date(d).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

  const itemValue = (it) =>
    (parseFloat(it.price) || 0) * (parseFloat(it.quantity) || 0) / Math.max(1, parseInt(it.price_unit, 10) || 1);

  // Stepper-Schritte aus echten Belegdaten
  const steps = [
    { key: 'created', label: t('bl.stepCreated'), state: 'done', meta: `${request.requester_name || ''} · ${fmtDate(request.created_at)}` },
    request.status === 'pending'
      ? { key: 'approval', label: t('bl.stepApproval'), state: 'current', meta: `${t('bl.waiting')} ${waitingSince(request, language)}` }
      : request.status === 'approved'
        ? { key: 'approval', label: t('bl.stepApproval'), state: 'done', meta: `${request.approver_comment ? `„${request.approver_comment}" · ` : ''}${fmtDate(request.processed_at)}` }
        : { key: 'approval', label: t('bl.stepRejected'), state: 'bad', meta: `${request.approver_comment ? `„${request.approver_comment}" · ` : ''}${fmtDate(request.processed_at)}` },
    request.sap_transfer_status === 'posted'
      ? { key: 'sap', label: t('bl.stepSap'), state: 'done', meta: `${request.sap_banfn} · ${fmtDate(request.sap_transferred_at)}` }
      : request.sap_transfer_status === 'failed'
        ? { key: 'sap', label: t('bl.stepSap'), state: 'bad', meta: request.sap_last_message || t('bl.sapFailed') }
        : { key: 'sap', label: t('bl.stepSap'), state: request.status === 'rejected' ? 'skipped' : 'open', meta: request.status === 'rejected' ? '—' : t('bl.sapOpen') },
  ];

  const dot = (state) => {
    if (state === 'done') return 'bg-emerald-500 border-emerald-500 text-white';
    if (state === 'current') return `bg-transparent border-amber-500 ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`;
    if (state === 'bad') return 'bg-red-500 border-red-500 text-white';
    return `bg-transparent ${isDarkMode ? 'border-white/25 text-white/40' : 'border-slate-300 text-slate-400'}`;
  };

  const metaRows = [
    [t('bl.colAnforderer'), request.requester_name],
    [t('bl.metaCreated'), fmtDate(request.created_at)],
    [t('bl.metaDeptKst'), [request.requester_department, request.requester_cost_center].filter(Boolean).join(' / ')],
    [t('bl.colLieferant'), banfVendor(request) || '—'],
    [t('bl.colMatkl'), banfMatkl(request) || '—'],
    [t('bl.colSap'), request.sap_banfn || '—'],
  ];

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Scrim */}
      <div className="absolute inset-0 bg-black/25" onClick={onClose} />

      {/* Panel */}
      <div className={`absolute top-0 right-0 bottom-0 w-full max-w-[460px] flex flex-col shadow-2xl animate-[slideIn_.18s_ease-out] ${
        isDarkMode ? 'bg-gray-900/95 border-l border-white/15 backdrop-blur-xl' : 'bg-white border-l border-slate-200'
      }`}>
        <style>{'@keyframes slideIn{from{transform:translateX(28px);opacity:.6}to{transform:translateX(0);opacity:1}}'}</style>

        {/* Kopf */}
        <div className={`px-5 pt-4 pb-3 border-b flex-shrink-0 ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`font-mono text-sm ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>BANF #{request.id}</span>
              <StatusPill status={status} isDarkMode={isDarkMode} t={t} />
            </div>
            <button onClick={onClose} className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-white/10 text-white/60' : 'hover:bg-slate-100 text-slate-400'}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
          <h2 className={`mt-1 text-lg font-extrabold leading-snug ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{banfSubject(request)}</h2>
          <p className={`mt-1 font-mono text-[22px] font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {fmtAmount(request.total_amount, request.currency)}
            <span className={`ml-2 font-sans text-xs font-normal ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>
              {t('bl.net')} · {items.length} {items.length === 1 ? t('bl.position') : t('bl.positions')}
            </span>
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Metagrid */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
            {metaRows.map(([label, value]) => (
              <div key={label}>
                <p className={`text-[10.5px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{label}</p>
                <p className={`text-sm mt-0.5 ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>{value || '—'}</p>
              </div>
            ))}
          </div>

          {/* Positionen */}
          <div className={`rounded-2xl border ${isDarkMode ? 'border-white/10 bg-gray-800/30' : 'border-slate-100 bg-slate-50'}`}>
            <p className={`px-4 pt-3 text-[10.5px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
              {t('bl.positions')}
            </p>
            <div className={`divide-y ${isDarkMode ? 'divide-white/5' : 'divide-slate-100'}`}>
              {items.map((it, i) => (
                <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`} title={it.description}>{it.description}</p>
                    <p className={`text-xs ${isDarkMode ? 'text-white/45' : 'text-slate-400'}`}>
                      {formatQuantity(it.quantity, it.unit)} {it.unit || 'ST'}
                      {(parseInt(it.price_unit, 10) || 1) !== 1 ? ` · PEINH ${it.price_unit}` : ''}
                    </p>
                  </div>
                  <span className={`font-mono text-sm ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>{fmtAmount(itemValue(it), it.currency)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Genehmigungsweg */}
          <div>
            <p className={`text-[10.5px] font-semibold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
              {t('bl.approvalPath')}
            </p>
            <div className="space-y-0">
              {steps.map((s, i) => (
                <div key={s.key} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center text-[11px] font-bold ${dot(s.state)}`}>
                      {s.state === 'done' ? '✓' : s.state === 'bad' ? '✕' : ''}
                    </div>
                    {i < steps.length - 1 && (
                      <div className={`w-0.5 flex-1 min-h-[18px] ${s.state === 'done' ? 'bg-emerald-500/60' : (isDarkMode ? 'bg-white/10' : 'bg-slate-200')}`} />
                    )}
                  </div>
                  <div className="pb-4">
                    <p className={`text-sm font-semibold ${s.state === 'bad' ? (isDarkMode ? 'text-red-300' : 'text-red-600') : (isDarkMode ? 'text-white/90' : 'text-slate-800')}`}>{s.label}</p>
                    <p className={`text-xs mt-0.5 ${s.state === 'current' ? (isDarkMode ? 'text-amber-300' : 'text-amber-600') : (isDarkMode ? 'text-white/45' : 'text-slate-400')}`}>{s.meta}</p>
                    {s.key === 'sap' && s.state === 'bad' && canDecide && (
                      <button onClick={() => onRetrySap(request.id)} disabled={processing}
                        className={`mt-1.5 px-3 py-1 text-xs font-medium rounded-full border transition-colors disabled:opacity-50 ${isDarkMode ? 'border-white/20 text-white/80 hover:bg-white/10' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                        {t('pr.sapRetry')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`px-5 py-3.5 border-t flex items-center gap-2 flex-shrink-0 ${isDarkMode ? 'border-white/10 bg-gray-900/60' : 'border-slate-100 bg-slate-50'}`}>
          {request.status === 'pending' && canDecide ? (
            <>
              <button onClick={() => onApprove(request.id)} disabled={processing}
                className="flex-1 h-[42px] rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 mr-1.5" /> {t('pr.approve')}
              </button>
              <button onClick={() => onReject(request.id)} disabled={processing}
                className={`px-4 h-[42px] rounded-full text-sm font-medium border transition-colors disabled:opacity-50 ${isDarkMode ? 'border-white/20 text-white/80 hover:bg-red-500/20 hover:text-red-200' : 'border-slate-300 text-slate-600 hover:bg-red-50 hover:text-red-600'}`}>
                {t('pr.reject')}
              </button>
            </>
          ) : (
            <button onClick={() => onReorder(request)} disabled={processing}
              className={`flex-1 h-[42px] rounded-full text-sm font-medium border transition-colors disabled:opacity-50 flex items-center justify-center ${isDarkMode ? 'border-white/20 text-white/85 hover:bg-white/10' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`}>
              <FilePlus className="w-4 h-4 mr-1.5" /> {t('pr.reorder')}
            </button>
          )}
          {canDelete && !request.deleted_at && (
          <button onClick={() => onDelete(request)} disabled={processing} title={t('pr.deleteRequest')}
            className={`w-[42px] h-[42px] rounded-full border flex items-center justify-center transition-colors disabled:opacity-50 ${isDarkMode ? 'border-white/15 text-white/60 hover:bg-red-500/20 hover:text-red-200' : 'border-slate-200 text-slate-400 hover:bg-red-50 hover:text-red-600'}`}>
            <Trash2 className="w-4 h-4" />
          </button>
          )}
        </div>
      </div>
    </div>
  );
};

/** Status-Pill mit Punkt (auch von der Tabelle genutzt). */
export const StatusPill = ({ status, isDarkMode, t }) => {
  const map = {
    pending: { label: t('bl.stPending'), cls: isDarkMode ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-50 text-amber-700', dotC: 'bg-amber-500' },
    approved: { label: t('bl.stApproved'), cls: isDarkMode ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-50 text-emerald-700', dotC: 'bg-emerald-500' },
    transferred: { label: t('bl.stTransferred'), cls: isDarkMode ? 'bg-blue-500/15 text-blue-300' : 'bg-brand/10 text-brand', dotC: 'bg-brand' },
    rejected: { label: t('bl.stRejected'), cls: isDarkMode ? 'bg-red-500/15 text-red-300' : 'bg-red-50 text-red-600', dotC: 'bg-red-500' },
    draft: { label: t('bl.stDraft'), cls: isDarkMode ? 'bg-white/10 text-white/60' : 'bg-slate-100 text-slate-500', dotC: 'bg-slate-400' },
    deleted: { label: t('bl.stDeleted'), cls: isDarkMode ? 'bg-white/10 text-white/45 line-through' : 'bg-slate-100 text-slate-400 line-through', dotC: 'bg-slate-400' },
  };
  const m = map[status] || map.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dotC}`} />
      {m.label}
    </span>
  );
};

export default BanfDetailDrawer;
